import { Link } from "react-router-dom";
import { DISCIPLINES, type DisciplineSlug } from "@jumpbean/shared";
import { Button, CharacterImage } from "../components";
import { usePageTitle } from "../hooks";

/**
 * AboutPage - Company story and mission page
 *
 * Features:
 * - Company mission and values
 * - Why we built JumpBean
 * - Team/founder story
 * - Call to action
 */
export function AboutPage() {
  usePageTitle("About JumpBean");

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-b from-magic-100 via-sunshine-50 to-white overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-luna/20 blur-3xl" />
        <div className="absolute bottom-20 right-10 w-48 h-48 rounded-full bg-petal/20 blur-3xl" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <span className="inline-block font-display font-bold text-body-lg text-magic-500 mb-4">
            Our Story
          </span>
          <h1 className="font-display font-extrabold text-display-lg md:text-display-xl text-neutral-900 mb-6">
            Learning should feel like{" "}
            <span className="text-magic-600">magic</span>
          </h1>
          <p className="font-body text-body-lg md:text-body-xl text-neutral-600 max-w-2xl mx-auto">
            We believe every preschooler deserves educational experiences that spark joy,
            build confidence, and create a lifelong love of learning.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Character illustration */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-luna/20 to-petal/20 rounded-full blur-3xl scale-150" />
                <div className="relative flex gap-4">
                  <CharacterImage
                    discipline="math"
                    expression="happy"
                    size="2xl"
                    floating
                    className="transform -rotate-6"
                  />
                  <CharacterImage
                    discipline="spelling"
                    expression="happy"
                    size="2xl"
                    floating
                    className="transform rotate-6 mt-8"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <h2 className="font-display font-extrabold text-display-md text-neutral-900 mb-6">
                Why we built JumpBean
              </h2>

              <div className="space-y-4 font-body text-body-lg text-neutral-700">
                <p>
                  As parents, educators, and licensed child play therapy counselors, we saw the same
                  thing you did: a sea of "educational" apps that were
                  either <span className="font-semibold">overwhelming</span>,{" "}
                  <span className="font-semibold">boring</span>, or{" "}
                  <span className="font-semibold">designed to maximize screen time, not learning</span>.
                </p>
                <p>
                  We wanted something different. An app where our preschoolers would actually learn—not
                  just tap mindlessly. Where they'd feel encouraged, not frustrated. Where we wouldn't
                  have to worry about ads, dark patterns, or our kids begging for in-app purchases.
                </p>
                <p>
                  So we built <span className="font-semibold text-magic-600">JumpBean</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-b from-sunshine-50 to-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-display font-extrabold text-display-md text-neutral-900 mb-4">
              What we believe
            </h2>
            <p className="font-body text-body-lg text-neutral-600 max-w-2xl mx-auto">
              Our values shape every decision we make—from the challenges we design to how
              we handle your data.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: "✨",
                title: "Joy over drilling",
                description:
                  "Learning happens best when kids are having fun. Every challenge is designed to delight first, teach second.",
              },
              {
                icon: "💚",
                title: "Encouragement only",
                description:
                  "No wrong answers, no red X marks, no failure states. Every attempt is celebrated. Confidence grows with every try.",
              },
              {
                icon: "🛡️",
                title: "Kids-first design",
                description:
                  "No ads. No child-facing purchases. No manipulative tactics. Your child's experience is sacred.",
              },
              {
                icon: "👨‍👩‍👧",
                title: "Parents as partners",
                description:
                  "You know your child best. We give you visibility into their progress and control over their experience.",
              },
              {
                icon: "📏",
                title: "Age-appropriate everything",
                description:
                  "Touch targets sized for little fingers. Sessions designed for preschool attention spans. Nothing overwhelming.",
              },
              {
                icon: "🌱",
                title: "Mastery over speed",
                description:
                  "There's no timer, no leaderboard, no pressure. Kids learn at their own pace, building real understanding.",
              },
            ].map((value) => (
              <div
                key={value.title}
                className="bg-white rounded-2xl p-8 shadow-playful border-2 border-neutral-100 hover:border-magic-200 transition-colors"
              >
                <span className="text-4xl mb-4 block">{value.icon}</span>
                <h3 className="font-display font-bold text-body-xl text-neutral-900 mb-2">
                  {value.title}
                </h3>
                <p className="font-body text-body-md text-neutral-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Five Friends Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-display font-extrabold text-display-md text-neutral-900 mb-4">
              Meet the magical guides
            </h2>
            <p className="font-body text-body-lg text-neutral-600 max-w-2xl mx-auto">
              Each character is a friend who makes learning feel like an adventure,
              not a chore.
            </p>
          </div>

          <div className="space-y-8">
            {(Object.entries(DISCIPLINES) as [DisciplineSlug, typeof DISCIPLINES.math][]).map(
              ([slug, discipline]) => {
                const characterDetails: Record<DisciplineSlug, { story: string; personality: string }> = {
                  math: {
                    story: "Luna lives in a castle made of stars, where numbers dance in the night sky.",
                    personality: "Patient, sparkly, and always ready to celebrate when you count something new.",
                  },
                  spelling: {
                    story: "Petal tends a magical garden where letters bloom into words.",
                    personality: "Gentle, nurturing, and believes every letter is a seed of possibility.",
                  },
                  patterns: {
                    story: "Prism builds rainbow bridges across the sky, finding patterns everywhere.",
                    personality: "Curious, colorful, and loves helping kids see the world in new ways.",
                  },
                  typing: {
                    story: "Whiskers discovered magic in the keyboard and now teaches the ancient art of typing.",
                    personality: "Wise, a bit silly, and makes every keystroke feel like casting a spell.",
                  },
                  programming: {
                    story: "Pixel is part owl, part robot, and loves solving puzzles with code.",
                    personality: "Clever, encouraging, and never gives up on a maze.",
                  },
                };

                const details = characterDetails[slug];

                return (
                  <div
                    key={slug}
                    className="flex flex-col md:flex-row items-center gap-8 bg-gradient-to-br from-neutral-50 to-white rounded-3xl p-8 border-2 border-neutral-100"
                  >
                    <div className="flex-shrink-0">
                      <CharacterImage
                        discipline={slug}
                        expression="happy"
                        size="2xl"
                        floating
                        glowing
                      />
                    </div>
                    <div className="text-center md:text-left">
                      <h3 className="font-display font-bold text-display-sm text-neutral-900 mb-2">
                        {discipline.characterName}
                      </h3>
                      <p className="font-display text-body-lg text-magic-600 mb-3">
                        {discipline.name} Guide
                      </p>
                      <p className="font-body text-body-md text-neutral-700 mb-2">
                        {details.story}
                      </p>
                      <p className="font-body text-body-sm text-neutral-500 italic">
                        {details.personality}
                      </p>
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-b from-magic-100 via-sunshine-50 to-sky-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-display font-extrabold text-display-md text-neutral-900 mb-4">
            Ready to start the adventure?
          </h2>
          <p className="font-body text-body-lg text-neutral-600 mb-8">
            Try JumpBean free and see the magic for yourself.
          </p>

          <Link to="/register">
            <Button size="lg" variant="primary">
              Start Free Today
            </Button>
          </Link>

          <p className="mt-6 font-body text-body-sm text-neutral-500">
            No credit card required. No commitment.
          </p>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
